import { Suspense, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AppShell,
  Burger,
  Group,
  NavLink,
  Text,
  Avatar,
  Menu,
  Divider,
  Center,
  Loader,
  Box,
  UnstyledButton,
  Alert,
  Stack,
  Badge,
} from '@mantine/core';
import { NoticesBanner } from './NoticesBanner';
import { PreviewBanner } from './PreviewBanner';
import { useDisclosure } from '@mantine/hooks';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { CustomerGuidedTour } from './CustomerGuidedTour';
import {
  IconLayoutDashboard,
  IconTicket,
  IconPackage,
  IconBox,
  IconCalendar,
  IconFileCertificate,
  IconInbox,
  IconSettings,
  IconLogout,
  IconShield,
  IconHistory,
  IconWorldWww,
  IconFileText,
  IconClipboard,
  IconHelp,
  IconWifi,
  IconShoppingCart,
  IconFileAnalytics,
  IconTimeline,
} from '@tabler/icons-react';
import { getPublicCustomerPortalStatus, logoutCustomer } from '../../lib/api.customer';
import { useCustomerStore } from '../../stores/customerStore';
import { PortalLocationFilter } from './PortalLocationFilter';
import { PortalCompanySwitcher } from './PortalCompanySwitcher';
import { PortalFeedbackRail } from './PortalFeedbackRail';
import { getTrackingClient } from '../../lib/tracking';
import { portalAreaState } from './portalModules';
import type { PortalModuleId } from '../../lib/api.customer';

const ALL_NAV_ITEMS = [
  { label: 'Dashboard', href: '/customer/dashboard', icon: IconLayoutDashboard, module: null, requiresContact: false, hideForCompany: false },
  { label: 'Tickets', href: '/customer/tickets', icon: IconTicket, module: 'tickets', requiresContact: false, hideForCompany: false },
  { label: 'Anfragen', href: '/customer/requests', icon: IconPackage, module: 'requests', requiresContact: false, hideForCompany: false },
  { label: 'Postfach', href: '/customer/inbox', icon: IconInbox, module: 'inbox', requiresContact: false, hideForCompany: false },
  { label: 'Shop', href: '/customer/shop', icon: IconShoppingCart, module: 'shop', requiresContact: false, hideForCompany: false },
  { label: 'Angebote', href: '/customer/quotes', icon: IconFileText, module: 'quotes', requiresContact: false, hideForCompany: true },
  { label: 'Serviceberichte', href: '/customer/submissions', icon: IconClipboard, module: 'submissions', requiresContact: false, hideForCompany: false },
  // Gemeinsame Standortbestimmung (Monat/Quartal/Jahr) — bewusst anders benannt als
  // die Einsatzberichte darüber, sonst sucht niemand das Richtige.
  { label: 'Unsere IT', href: '/customer/reports', icon: IconFileAnalytics, module: 'reports', requiresContact: false, hideForCompany: false },
  { label: 'Assets & Geräte', href: '/customer/assets', icon: IconBox, module: 'assets', requiresContact: false, hideForCompany: false },
  { label: 'Verträge & Lizenzen', href: '/customer/contracts', icon: IconFileCertificate, module: 'contracts', requiresContact: false, hideForCompany: false },
  { label: 'Termine', href: '/customer/appointments', icon: IconCalendar, module: 'appointments', requiresContact: false, hideForCompany: false },
  { label: 'Projekte', href: '/customer/projects', icon: IconTimeline, module: 'projects', requiresContact: false, hideForCompany: false },
  { label: 'Trust Center', href: '/customer/trust', icon: IconShield, module: 'trust', requiresContact: false, hideForCompany: false },
  { label: 'Protokoll', href: '/customer/audit', icon: IconHistory, module: 'audit', requiresContact: false, hideForCompany: false },
  { label: 'DNS-Zonen', href: '/customer/dns', icon: IconWorldWww, module: 'dns', requiresContact: false, hideForCompany: false },
] satisfies Array<{
  label: string;
  href: string;
  icon: typeof IconTicket;
  /** null = immer erreichbar (Startseite) */
  module: PortalModuleId | null;
  requiresContact: boolean;
  hideForCompany: boolean;
}>;

export function CustomerLayout() {
  const [opened, { toggle }] = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const contact = useCustomerStore((s) => s.contact);

  const isCompanySession = contact?.hasContact === false;
  const preview = contact?.preview ?? null;
  // showQuotes wird serverseitig im /me-Endpoint aus den Link-Rechten abgeleitet
  // (Rolle ADMIN oder "einkauf"-Paket) bzw. dem Firmen-Schalter.
  const showQuotes = contact?.companySessionConfig?.showQuotes === true;
  // Nicht freigegebene Bereiche bleiben stehen, aber gesperrt: „Demnächst", wenn die Firma
  // sie noch nicht hat — wer weiß, dass es kommt, fragt nicht nach, ob etwas kaputt ist.
  // „Kein Zugriff", wenn sie für diese Person abgeschaltet sind.
  const navItems = ALL_NAV_ITEMS.filter((item) => {
    if (item.requiresContact && !contact?.hasContact) return false;
    if (isCompanySession && item.hideForCompany && !showQuotes) return false;
    return true;
  }).map((item) => ({
    ...item,
    state: item.module === null ? ('open' as const) : portalAreaState(item.module, contact),
  }));
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  const { data: publicStatus } = useQuery({
    queryKey: ['customer-portal-public-status'],
    queryFn: getPublicCustomerPortalStatus,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  // Post-login: use personalized notices from the store; pre-login: fall back to public notices
  const noticesFromStore = contact?.portalNotices;
  const notices = noticesFromStore ?? publicStatus?.notices ?? [];

  async function handleLogout() {
    // Task 1.4 — Queue leeren bevor Session ungültig wird
    void getTrackingClient().flush(true);
    try {
      await logoutCustomer();
    } finally {
      useCustomerStore.getState().clear();
      // Die Mitarbeiter-Ansicht kam als eigener Tab aus dem ServiceHub — zurück dorthin.
      // Lässt der Browser das Schließen nicht zu, bleibt es bei der Anmeldeseite.
      if (preview) window.close();
      navigate('/customer/login');
    }
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      {/* Header */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Link to="/customer/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src="/fixbyte-logo.svg" alt="FixByte IT" style={{ height: 28, width: 'auto' }} />
              {contact?.name && (
                <Text size="sm" c="dimmed" hiddenFrom="xs"> · {contact.name}</Text>
              )}
            </Link>
            {preview && (
              <Badge color="orange" variant="filled" style={{ textTransform: 'none' }}>
                Mitarbeiter-Ansicht
              </Badge>
            )}
          </Group>

          <Group gap="sm">
            <PortalCompanySwitcher />
            <PortalLocationFilter />

            <Menu shadow="md" width={180} position="bottom-end">
              <Menu.Target>
                <UnstyledButton aria-label="Benutzer-Menü">
                  <Avatar radius="xl" size="sm" color="orange" name={contact?.name ?? 'K'} />
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{contact?.email ?? ''}</Menu.Label>
                {!isCompanySession && (
                  <Menu.Item
                    leftSection={<IconSettings size={14} />}
                    onClick={() => navigate('/customer/settings')}
                  >
                    Einstellungen
                  </Menu.Item>
                )}
                <Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={14} />}
                  onClick={handleLogout}
                >
                  {preview ? 'Ansicht beenden' : 'Abmelden'}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Sidebar */}
      <AppShell.Navbar p="xs">
        {navItems.map((item) => (
          <NavLink
            key={item.href}
            label={item.label}
            leftSection={<item.icon size={18} />}
            rightSection={
              item.state !== 'open' ? (
                <Badge size="xs" variant="light" color="gray" style={{ textTransform: 'none' }}>
                  {item.state === 'coming-soon' ? 'Demnächst' : 'Kein Zugriff'}
                </Badge>
              ) : undefined
            }
            component={Link}
            to={item.href}
            active={location.pathname.startsWith(item.href)}
            styles={{
              root: { borderRadius: 8, marginBottom: 2, opacity: item.state !== 'open' ? 0.55 : 1 },
            }}
          />
        ))}

        <Divider my="md" />

        <NavLink
          label="Hilfe / FAQ"
          leftSection={<IconHelp size={18} />}
          component={Link}
          to="/customer/help"
          active={location.pathname.startsWith('/customer/help')}
          styles={{ root: { borderRadius: 8, marginBottom: 2 } }}
        />

        {!isCompanySession && (
          <NavLink
            label="Einstellungen"
            leftSection={<IconSettings size={18} />}
            component={Link}
            to="/customer/settings"
            active={location.pathname.startsWith('/customer/settings')}
            styles={{ root: { borderRadius: 8 } }}
          />
        )}
      </AppShell.Navbar>

      {/* Content */}
      <AppShell.Main>
        <Stack gap="md">
          {preview && (
            <PreviewBanner
              preview={preview}
              contactName={contact?.name ?? null}
              companyName={contact?.companyName}
              onEnd={handleLogout}
            />
          )}
          {isOffline && (
            <Alert color="gray" variant="light" icon={<IconWifi size={18} />}>
              Keine Internetverbindung – Inhalte können nicht geladen werden.
            </Alert>
          )}
          <NoticesBanner notices={notices} />
          <Suspense
            fallback={
              <Center h={200}>
                <Loader size="sm" />
              </Center>
            }
          >
            <Outlet />
          </Suspense>
        </Stack>

        {contact ? <PortalFeedbackRail /> : null}

        <Box component="footer" mt="xl" py="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          <Group gap="lg" justify="center" wrap="wrap">
            <Text component={Link} to="/customer/trust" size="xs" c="dimmed" style={{ textDecoration: 'none' }}>
              Trust Center
            </Text>
            <Text component={Link} to="/customer/settings" size="xs" c="dimmed" style={{ textDecoration: 'none' }}>
              Einstellungen
            </Text>
            <Text component={Link} to="/customer/settings/export" size="xs" c="dimmed" style={{ textDecoration: 'none' }}>
              Datenexport
            </Text>
            <Text component={Link} to="/customer/help" size="xs" c="dimmed" style={{ textDecoration: 'none' }}>
              Hilfe / FAQ
            </Text>
          </Group>
        </Box>
      </AppShell.Main>

      {/* Über allen Seiten, damit der Rundgang beim Navigieren nicht abreißt. */}
      <CustomerGuidedTour />
    </AppShell>
  );
}
